import { useState } from 'react';
import { toast } from 'sonner';
import type { DocumentRenderSection, DocumentRenderTemplate } from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { assertToolSucceeded } from '@/api/templates-api';
import {
  useDeleteTemplateSection,
  useMergeTemplateSections,
  useSplitTemplateSection,
  useTemplateRead,
  useUpdateTemplateSection,
} from '@/hooks/useTemplateQueries';
import { Lightbulb, MoreHorizontal, Pencil } from 'lucide-react';
import { TemplateEntryDefEditor } from './TemplateEntryDefEditor';
import { TemplateFieldsEditor } from './TemplateFieldsEditor';

interface TemplateSectionChromeProps {
  section: DocumentRenderSection;
  template: DocumentRenderTemplate;
  isFirstSection: boolean;
}

function surfaceError(error: unknown): void {
  toast.error(error instanceof Error ? error.message : 'The action failed.');
}

// Everything shown here is a backend fact (DocumentRenderSection.templateSection); every change
// is one of the governed templates.* tools. Draft-only controls are gated by template.status —
// a render field, not a derivation.
export function TemplateSectionChrome({ section, template, isFirstSection }: TemplateSectionChromeProps) {
  const facts = section.templateSection;
  const isDraft = template.status === 'DRAFT';
  const [editingGuidance, setEditingGuidance] = useState(false);
  const [guidanceDraft, setGuidanceDraft] = useState('');

  const updateSection = useUpdateTemplateSection();
  const mergeSections = useMergeTemplateSections();
  const splitSection = useSplitTemplateSection();
  const deleteSection = useDeleteTemplateSection();
  // Block previews for the Split submenu come from the governed read, not local parsing.
  const templateRead = useTemplateRead(
    isDraft && facts !== null && facts.blockIds.length > 1
      ? { templateId: template.templateContentId }
      : null
  );

  if (facts === null) return null;

  const saveGuidance = async () => {
    try {
      assertToolSucceeded(
        await updateSection.mutateAsync({
          sectionDocumentId: section.id,
          guidance: guidanceDraft.trim(),
        })
      );
      setEditingGuidance(false);
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleMerge = async () => {
    try {
      assertToolSucceeded(await mergeSections.mutateAsync({ sectionDocumentId: section.id }));
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleSplitAt = async (atBlockId: number) => {
    try {
      assertToolSucceeded(
        await splitSection.mutateAsync({ sectionDocumentId: section.id, atBlockId })
      );
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete section '${section.title || section.code}'? Its blocks become uncovered.`)) {
      return;
    }
    try {
      assertToolSucceeded(await deleteSection.mutateAsync({ sectionDocumentId: section.id }));
    } catch (error) {
      surfaceError(error);
    }
  };

  const splitCandidates =
    templateRead.data?.blocks?.filter(
      (block) => facts.blockIds.includes(block.id) && block.id !== facts.blockIds[0]
    ) ?? [];

  const focusTitleEditor = () => {
    const heading = window.document.querySelector<HTMLElement>(
      `[data-section-id="${section.id}"] h1, [data-section-id="${section.id}"] h2, [data-section-id="${section.id}"] h3`
    );
    heading?.click();
  };

  return (
    <div
      data-testid="template-section-chrome"
      className="mt-2 space-y-2 border-l-2 border-muted pl-3"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {facts.tables.map((table) => (
          <span key={table.blockId} className="contents">
            <Badge variant="secondary">
              {table.mode === 'repeat' ? 'Repeats per entry' : 'Fixed rows'}
            </Badge>
            {table.photoColumn !== null ? (
              <Badge variant="secondary">Photo column: {table.photoColumn}</Badge>
            ) : null}
            {table.bands ? <Badge variant="secondary">Band rows</Badge> : null}
          </span>
        ))}
        {facts.placeholderCount > 0 ? (
          <Badge variant="secondary">{facts.placeholderCount} placeholder(s)</Badge>
        ) : null}
        {facts.entry === null ? (
          <TemplateFieldsEditor section={section} templateStatus={template.status} />
        ) : null}
        <TemplateEntryDefEditor section={section} templateStatus={template.status} />
        {facts.untitled ? (
          <button type="button" onClick={focusTitleEditor}>
            <Badge variant="outline">Untitled</Badge>
          </button>
        ) : null}
        {isDraft ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-6 w-6" aria-label="Section actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {!isFirstSection ? (
                <DropdownMenuItem onSelect={() => void handleMerge()}>
                  Merge into previous
                </DropdownMenuItem>
              ) : null}
              {splitCandidates.length > 0 ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Split at…</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {splitCandidates.map((block) => (
                      <DropdownMenuItem key={block.id} onSelect={() => void handleSplitAt(block.id)}>
                        #{block.id} — {block.preview.slice(0, 40) || block.kind}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : null}
              <DropdownMenuItem onSelect={() => void handleDelete()}>Delete section</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {editingGuidance ? (
        <div className="space-y-2">
          <Textarea
            value={guidanceDraft}
            onChange={(event) => setGuidanceDraft(event.target.value)}
            rows={2}
            placeholder="One line on how a write-up fills this layout"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void saveGuidance()} disabled={updateSection.isPending}>
              Save guidance
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingGuidance(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="flex items-start gap-1.5 text-left text-sm italic text-muted-foreground"
          onClick={() => {
            setGuidanceDraft(facts.guidance);
            setEditingGuidance(true);
          }}
        >
          <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">
            {facts.guidance.trim().length > 0 ? `Guidance: ${facts.guidance}` : 'No guidance yet'}
          </span>
          <Pencil className="mt-0.5 h-3 w-3 shrink-0 opacity-60" aria-hidden />
        </button>
      )}
    </div>
  );
}
